import { RefObject } from 'react';
import { Button } from '@mui/material';
import { SavedThumbnail } from '../../../../data/atom';
import {
  TALENT_ARR,
  TALENT_CHIPS,
  VIDEO_RECORD_TYPE,
  ON_KIDS_ANALYSIS_THUMBNAIL,
} from '../../../../data/define';
import Url from '../../../../data/url';
import AnalysisResultChart from '../../../../component/register/AnalysisResultChart';
import ImgNoThumbnail from '../../../../assets/image/fallback_thumbnail.png';
import IcStars from '../../../../assets/image/ic_video_result_starts.svg';
import IcLanguage from '../../../../assets/image/intelligence/ic_language.svg';
import IcMusic from '../../../../assets/image/intelligence/ic_music.svg';
import IcPhysical from '../../../../assets/image/intelligence/ic_physical.svg';
import IcMath from '../../../../assets/image/intelligence/ic_math.svg';
import IcSpace from '../../../../assets/image/intelligence/ic_space.svg';
import IcRelationship from '../../../../assets/image/intelligence/ic_relationship.svg';
import IcReflection from '../../../../assets/image/intelligence/ic_reflection.svg';
import IcNature from '../../../../assets/image/intelligence/ic_nature.svg';
import type { AnalysisLocationState, SecondAnalysisItem } from '../index';

const INTELLIGENCE_ICONS: Record<number, string> = {
  1: IcLanguage,
  2: IcMusic,
  3: IcPhysical,
  4: IcMath,
  5: IcSpace,
  6: IcRelationship,
  7: IcReflection,
  8: IcNature,
};

interface ReportDetailQna {
  intelligence: number;
  score: number;
  percentile: number;
}

interface ReportDetailDaily {
  SecondAnalysis: SecondAnalysisItem[];
}

interface AnalysisSuccessViewProps {
  reportDetail: ReportDetailDaily | ReportDetailQna | null;
  analysisType: number;
  bestScore: SecondAnalysisItem | null;
  imgRef: RefObject<HTMLImageElement>;
  state: AnalysisLocationState | null;
  savedThumbnail: SavedThumbnail;
  onResetAndNavigate: (to: string) => void;
}

export default function AnalysisSuccessView({
  reportDetail,
  analysisType,
  bestScore,
  imgRef,
  state,
  savedThumbnail,
  onResetAndNavigate,
}: AnalysisSuccessViewProps) {
  const dailyDetail =
    analysisType === VIDEO_RECORD_TYPE.DAILY ? (reportDetail as ReportDetailDaily | null) : null;
  const qnaDetail =
    analysisType === VIDEO_RECORD_TYPE.QNA ? (reportDetail as ReportDetailQna | null) : null;

  return (
    <div className="relative w-full h-full flex flex-col items-center justify-end p-5">
      <style>
        {`
          @keyframes confettiLeft {
            0% {
              opacity: 0;
              transform: translateX(-100px) translateY(-50px) scaleY(-1) rotate(-45deg);
            }
            20% {
              opacity: 1;
              transform: translateX(0) translateY(0) scaleY(-1) rotate(0deg);
            }
            100% {
              opacity: 0;
              transform: translateX(20px) translateY(30px) scaleY(-1) rotate(15deg);
            }
          }
          @keyframes confettiRight {
            0% {
              opacity: 0;
              transform: translateX(100px) translateY(-50px) scaleX(-1) rotate(45deg);
            }
            20% {
              opacity: 1;
              transform: translateX(0) translateY(0) scaleX(-1) rotate(0deg);
            }
            100% {
              opacity: 0;
              transform: translateX(-20px) translateY(30px) scaleX(-1) rotate(-15deg);
            }
          }
          @keyframes confettiCenter {
            0% {
              opacity: 0;
              transform: translateY(-80px) scale(0.5);
            }
            15% {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
            100% {
              opacity: 0;
              transform: translateY(40px) scale(0.8);
            }
          }
          .confetti-left {
            animation: confettiLeft 2s ease-out forwards;
          }
          .confetti-right {
            animation: confettiRight 2s ease-out 0.3s forwards;
          }
          .confetti-center {
            animation: confettiCenter 2s ease-out 0.6s forwards;
          }
        `}
      </style>
      <img
        src={IcStars}
        alt="Stars"
        className="absolute top-2 left-2 z-2 confetti-left"
        style={{ transform: 'scaleY(-1)' }}
      />
      <img
        src={IcStars}
        alt="Stars"
        className="absolute top-[20%] right-2 z-2 confetti-right"
        style={{ transform: 'scaleX(-1)' }}
      />
      <img src={IcStars} alt="Stars" className="absolute top-[35%] left-2 z-2 confetti-center" />
      <div
        className="absolute top-0 left-0 right-0 bottom-0 w-full h-full z-1"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)' }}
      />
      <div className="z-2 absolute top-[10%] inset-0 text-center text-[24px] font-bold leading-[30px] text-[#FFFFFF] py-5">
        <div>
          AI가 아이의 재능을
          <br />
          분석 완료하였어요!
        </div>
        {reportDetail && (
          <div className="text-[18px] font-semibold leading-[30px] text-[#FFFFFF] mt-2 bg-[#42A9AA] inline-block p-1 px-3 rounded-[5px] shadow-md">
            {analysisType === VIDEO_RECORD_TYPE.DAILY ? (
              <>
                가장 높은지능 {TALENT_ARR.find((i) => i.id === bestScore?.intelligence)?.label} 상위{' '}
                {bestScore?.percentile}%
              </>
            ) : (
              <>상위 {qnaDetail?.percentile ?? 'unknown'}%</>
            )}
          </div>
        )}
      </div>
      {dailyDetail && (
        <div className="w-full px-5 mb-5 relative z-2 mx-5 rounded-[10px] min-h-[50px] text-[16px] font-semibold leading-[30px] text-[#FFF] py-2.5">
          <div className="w-full">
            <div
              className="grid grid-cols-2 gap-1 w-full mb-4"
              style={{
                gridTemplateColumns:
                  dailyDetail.SecondAnalysis?.length < 2 ? '1fr' : 'repeat(2, 1fr)',
              }}
            >
              {dailyDetail.SecondAnalysis?.map((item) => (
                <AnalysisResultChart
                  key={item.intelligence}
                  intelligence={item.intelligence}
                  score={item.score}
                />
              ))}
            </div>
          </div>
        </div>
      )}
      {qnaDetail && (
        <>
          <div className="w-full text-white text-left font-bold text-[18px] mb-3 z-2">
            지능별 분석결과
          </div>
          {(() => {
            const talent = TALENT_ARR.find((t) => t.id === qnaDetail.intelligence);
            const icon = INTELLIGENCE_ICONS[qnaDetail.intelligence] ?? null;
            return (
              <div className="w-full mb-5 z-2">
                <div
                  className="w-full z-2 flex justify-start items-center px-3 rounded-lg h-[45px] text-[14px] font-normal leading-[30px] text-[#FFFFFF] py-[7px] mb-3"
                  style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
                >
                  {icon && <img src={icon} alt="Icon" className="w-[19px] h-[21px] mr-2" />}
                  <div>{talent?.description}</div>
                </div>
                <AnalysisResultChart
                  intelligence={qnaDetail.intelligence}
                  score={qnaDetail.score}
                />
                <div className="w-full flex justify-start items-center gap-2 flex-wrap mt-4">
                  {TALENT_CHIPS.find((t) => t.id === qnaDetail.intelligence)?.labels.map(
                    (label) => (
                      <div
                        key={label}
                        className="bg-[rgba(0,0,0,0.5)] text-white rounded-2xl text-[12px] font-normal px-2 py-1.5 whitespace-nowrap"
                      >
                        #{label}
                      </div>
                    )
                  )}
                </div>
              </div>
            );
          })()}
        </>
      )}
      <Button
        variant="contained"
        className="relative z-2"
        fullWidth
        onClick={() => onResetAndNavigate(Url.Report)}
      >
        리포트 확인
      </Button>
      <div className="absolute top-0 bottom-0 left-0 right-0 w-full flex flex-col items-center justify-center overflow-hidden">
        <img
          ref={imgRef}
          src={
            state?.thumbnail ||
            savedThumbnail?.thumbnail ||
            sessionStorage.getItem(ON_KIDS_ANALYSIS_THUMBNAIL) ||
            ImgNoThumbnail
          }
          alt="Selected"
          className="object-cover max-h-full w-full h-full"
        />
      </div>
    </div>
  );
}
